import React, { useState, useEffect } from "react";
import { ActivityDefinition } from "../../types";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TagIcon,
  UserIcon,
  CalendarIcon,
  ArchiveBoxIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/apiService";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

interface ActivityLibraryProps {
  onSelectActivity?: (activity: ActivityDefinition) => void;
  onCreateNew?: () => void;
  selectionMode?: boolean;
  selectedActivityIds?: number[];
  multiSelect?: boolean;
}

export const ActivityLibrary: React.FC<ActivityLibraryProps> = ({
  onSelectActivity,
  onCreateNew,
  selectionMode = false,
  selectedActivityIds = [],
  multiSelect = false,
}) => {
  const { t } = useTranslation(["activities", "common"]);
  const [activities, setActivities] = useState<ActivityDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [selectedActivities, setSelectedActivities] =
    useState<number[]>(selectedActivityIds);

  // Load activities on mount
  useEffect(() => {
    loadActivities();
  }, []);

  // Load data based on filters
  useEffect(() => {
    loadActivities();
  }, [searchTerm, selectedTag, showOnlyMine]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        tag: selectedTag || undefined,
        onlyMine: showOnlyMine || undefined,
      };

      const result = await apiService.getAllActivities(params);
      setActivities(result || []);
    } catch (error) {
      toast.error(t("activities:failedToLoadActivities"));
    } finally {
      setLoading(false);
    }
  };

  const handleActivitySelect = async (activity: ActivityDefinition) => {
    if (selectionMode) {
      if (multiSelect) {
        const newSelected = selectedActivities.includes(activity.id)
          ? selectedActivities.filter((id) => id !== activity.id)
          : [...selectedActivities, activity.id];
        setSelectedActivities(newSelected);
      } else {
        // New pending system: Send activity data directly without duplicating
        // The WorkflowEditor will create a pending node
        try {
          // Fetch full activity data with forms
          const fullActivity = await apiService.getActivityById(activity.id);
          onSelectActivity?.(fullActivity);
          toast.success(
            t("activities:activityAddedToWorkflow", { title: activity.title })
          );
        } catch (error) {
          toast.error(t("activities:failedToLoadActivityData"));
        }
      }
    } else {
      onSelectActivity?.(activity);
    }
  };

  const duplicateActivityForWorkflow = async (
    activity: ActivityDefinition
  ): Promise<ActivityDefinition> => {
    try {
      const response = await fetch(`/api/activities/${activity.id}/duplicate`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to duplicate activity");
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      throw error;
    }
  };

  const handleDuplicateActivity = async (
    activityId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    try {
      const response = await fetch(`/api/activities/${activityId}/duplicate`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("activities:activityDuplicatedSuccessfully"));
        loadActivities();
      } else {
        toast.error(t("activities:failedToDuplicateActivity"));
      }
    } catch (error) {
      toast.error(t("activities:errorDuplicatingActivity"));
    }
  };

  const handleArchiveActivity = async (
    activityId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (!window.confirm(t("activities:confirmArchiveActivity"))) return;

    try {
      const response = await fetch(`/api/activities/${activityId}/archive`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("activities:activityArchivedSuccessfully"));
        loadActivities();
      } else {
        toast.error(t("activities:failedToArchiveActivity"));
      }
    } catch (error) {
      toast.error(t("activities:errorArchivingActivity"));
    }
  };

  const handleDeleteActivity = async (
    activityId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (!window.confirm(t("activities:confirmDeleteActivity"))) return;

    try {
      const response = await fetch(`/api/activities/${activityId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("activities:activityDeletedSuccessfully"));
        loadActivities();
      } else {
        toast.error(t("activities:failedToDeleteActivity"));
      }
    } catch (error) {
      toast.error(t("activities:errorDeletingActivity"));
    }
  };

  const getAvailableTags = () => {
    const tags = new Set<string>();

    activities.forEach((activity) => {
      if (activity.tags) {
        activity.tags.split(",").forEach((tag) => tags.add(tag.trim()));
      }
    });

    return Array.from(tags);
  };

  const getActivityFormsSummary = (activity: ActivityDefinition): string => {
    const formCount = activity.activityForms?.length || 0;
    if (formCount === 0) return t("activities:noFormsConfigured");

    const formTitles =
      activity.activityForms
        ?.map((af) => af.form.title)
        .slice(0, 2)
        .join(", ") || "";

    const formWord =
      formCount !== 1 ? t("activities:forms") : t("activities:form");
    return `${formCount} ${formWord}: ${formTitles}${
      formCount > 2 ? "..." : ""
    }`;
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return t("activities:noEstimate");
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {t("activities:activityLibrary")}
        </h2>
        {!selectionMode && (
          <button
            onClick={onCreateNew}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("activities:createNewActivity")}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("activities:searchActivities")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Tag Filter */}
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t("activities:allTags")}</option>
          {getAvailableTags().map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>

        {/* My Activities Toggle */}
        <div className="flex items-center">
          <input
            id="only-mine-activities"
            type="checkbox"
            checked={showOnlyMine}
            onChange={(e) => setShowOnlyMine(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label
            htmlFor="only-mine-activities"
            className="ml-2 text-sm text-gray-700"
          >
            {t("activities:onlyMyActivities")}
          </label>
        </div>
      </div>

      {/* Activities Grid */}
      <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity) => (
              <div
                key={activity.id}
                onClick={() => handleActivitySelect(activity)}
                className={`group relative bg-white rounded-lg border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedActivities.includes(activity.id)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                style={{
                  borderLeftColor: activity.color || "#6B7280",
                  borderLeftWidth: "4px",
                }}
              >
                {/* Selection indicator */}
                {selectionMode && (
                  <div className="absolute top-3 right-3">
                    <div
                      className={`h-4 w-4 rounded-full border-2 ${
                        selectedActivities.includes(activity.id)
                          ? "bg-blue-600 border-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedActivities.includes(activity.id) && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-2 w-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Header */}
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    {activity.icon ? (
                      <span className="text-2xl mr-2">{activity.icon}</span>
                    ) : (
                      <CubeIcon className="h-6 w-6 mr-2 text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 flex-1">
                      {activity.title}
                    </h3>
                  </div>
                  {activity.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {activity.description}
                    </p>
                  )}
                </div>

                {/* Activity Details */}
                <div className="space-y-2 mb-4">
                  <div className="text-xs text-gray-500">
                    {getActivityFormsSummary(activity)}
                  </div>

                  <div className="flex items-center text-xs text-gray-500">
                    <UserIcon className="h-3 w-3 mr-1" />
                    {activity.createdByName || t("activities:unknown")}
                    <span className="mx-2">•</span>
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {new Date(activity.createdAt).toLocaleDateString()}
                  </div>

                  <div className="flex items-center text-xs text-gray-500">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {formatDuration(activity.estimatedDurationMinutes)}
                    <span className="mx-2">•</span>
                    <span>v{activity.version}</span>
                  </div>

                  {/* Activity Properties */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {activity.requiresApproval && (
                      <span className="inline-block bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs">
                        {t("activities:requiresApproval")}
                      </span>
                    )}
                  </div>

                  {activity.tags && (
                    <div className="flex items-center text-xs text-gray-500 mt-2">
                      <TagIcon className="h-3 w-3 mr-1" />
                      <div className="flex flex-wrap gap-1">
                        {activity.tags.split(",").map((tag) => (
                          <span
                            key={tag}
                            className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                          >
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {!selectionMode && (
                  <div className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleDuplicateActivity(activity.id, e)}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title={t("activities:duplicateActivity")}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleArchiveActivity(activity.id, e)}
                      className="p-1 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                      title={t("activities:archiveActivity")}
                    >
                      <ArchiveBoxIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteActivity(activity.id, e)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title={t("activities:deleteActivityAction")}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Forms Count Badge */}
                {activity.activityForms &&
                  activity.activityForms.length > 0 && (
                    <div className="absolute bottom-3 right-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {activity.activityForms.length}{" "}
                        {activity.activityForms.length !== 1
                          ? t("activities:forms")
                          : t("activities:form")}
                      </span>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>

      {/* Empty State */}
      {activities.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <CubeIcon className="mx-auto h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("activities:noActivitiesFound")}
          </h3>
          <p className="text-gray-500">
            {searchTerm || selectedTag
              ? t("activities:tryAdjustingFilters")
              : t("activities:createFirstActivityToStart")}
          </p>
        </div>
      )}

      {/* Selection Summary */}
      {selectionMode && multiSelect && selectedActivities.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          {selectedActivities.length}{" "}
          {selectedActivities.length !== 1
            ? t("activities:activitiesSelected")
            : t("activities:activitySelected")}
        </div>
      )}

      {/* Activity Editor Modal - No longer used */}
    </div>
  );
};
